import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Typography, CircularProgress, useMediaQuery, useTheme, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ChannelSidebar from './components/ChannelSidebar';
import ChatWindow from './components/ChatWindow';
import CreateChannelDialog from './components/CreateChannelDialog';
import InviteUserDialog from './components/InviteUserDialog';
import RemoveUserDialog from './components/RemoveUserDialog';
import { channel as channelApi, message as messageApi } from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../components/auth/AuthModule';
import socketManager from '../../utils/socketManager';

const CommsPage = () => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [teamChannels, setTeamChannels] = useState([]);
  const [privateChannels, setPrivateChannels] = useState([]);
  const [lockedChannels, setLockedChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(!isMobile);
  const [updatingChannels, setUpdatingChannels] = useState(false);
  const [creatingDirectMessage, setCreatingDirectMessage] = useState(false);
  const [refetchOnSocketEvents, setRefetchOnSocketEvents] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  
  // Use refs to access current state values in event handlers without dependency issues
  const privateChannelsRef = useRef(privateChannels);
  // Update ref whenever privateChannels state changes
  useEffect(() => {
    privateChannelsRef.current = privateChannels;
  }, [privateChannels]);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      const response = await channelApi.getUserChannels();
      
      // Store the current selection ID before updating
      const currentSelectedId = selectedChannel?._id;
      
      // Process all channels to remove duplicate members
      const processedChannels = response.channels?.map(channel => ({
        ...channel,
        members: channel.members ? deduplicateMembers(channel.members) : []
      })) || [];
      
      const processedTeamChannels = response.teamChannels?.map(channel => ({
        ...channel,
        members: channel.members ? deduplicateMembers(channel.members) : []
      })) || [];
      
      const processedPrivateChannels = response.privateChannels?.map(channel => ({
        ...channel,
        members: channel.members ? deduplicateMembers(channel.members) : []
      })) || [];
      
      const processedLockedChannels = response.lockedChannels?.map(channel => ({
        ...channel,
        members: channel.members ? deduplicateMembers(channel.members) : []
      })) || [];
      
      setChannels(processedChannels);
      setTeamChannels(processedTeamChannels);
      setPrivateChannels(processedPrivateChannels);
      setLockedChannels(processedLockedChannels);
      
      // Only select a channel if:
      // 1. There is no currently selected channel, AND
      // 2. This is the initial load
      const isInitialLoad = loading && !currentSelectedId;
      
      if (isInitialLoad) {
        console.log('Initial load, selecting first team channel if available');
        
        // Prioritize team channels over private channels (DMs)
        if (processedTeamChannels.length > 0) {
          // Select the first team channel
          setSelectedChannel(processedTeamChannels[0]);
        } else if (processedChannels.length > 0) {
          // Fall back to first channel if no team channels available
          setSelectedChannel(processedChannels[0]);
        }
      } else if (currentSelectedId) {
        // If a channel is already selected, just update its data if needed
        const updatedChannel = processedChannels.find(ch => ch._id === currentSelectedId);
        if (updatedChannel && JSON.stringify(updatedChannel) !== JSON.stringify(selectedChannel)) {
          setSelectedChannel(updatedChannel);
        }
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
      toast.error(t('error.fetchingData'));
    } finally {
      setLoading(false);
    }
  };
  
  // Utility function to deduplicate members by ID
  const deduplicateMembers = (members) => {
    const uniqueMembers = [];
    const seenIds = new Set();
    
    for (const member of members) {
      const memberId = member._id || member;
      if (!seenIds.has(memberId)) {
        seenIds.add(memberId);
        uniqueMembers.push(member);
      }
    }
    
    return uniqueMembers;
  };

  useEffect(() => {
    fetchChannels();
    
    // Initialize socket and join user-specific room if user is logged in
    if (user && user._id) {
      console.log('Joining user-specific room:', user._id);
      socketManager.joinUserRoom(user._id);
    }
    
    // Listen for new direct messages
    const handleNewDirectMessage = (data) => {
      try {
        console.log('New direct message received:', data);
        
        // Check if the message is for the current user
        const isForCurrentUser = user && data.channel && data.channel.members && 
          data.channel.members.some(member => {
            const memberIdMatches = 
              member._id === user._id || 
              String(member._id) === String(user._id);
            
            return memberIdMatches;
          });
        
        console.log('Is for current user:', isForCurrentUser);
        
        if (isForCurrentUser) {
          console.log('Processing new DM channel');
          
          // Get the channel object from the event data
          const newChannel = data.channel;
          
          // Check if this channel is already in our privateChannels array
          // Use the ref instead of the state to avoid dependency issues
          const channelExists = privateChannelsRef.current.some(ch => ch._id === newChannel._id);
          
          if (!channelExists) {
            console.log('Adding new DM channel to private channels list');
            // Add this channel to the privateChannels array immediately
            setPrivateChannels(prevChannels => [newChannel, ...prevChannels]);
            
            // Also add to all channels list
            setChannels(prevChannels => [newChannel, ...prevChannels]);
          }
          
          // Still do a refresh to ensure consistency, but don't rely on it for UI updates
          fetchChannels();
          
          // If the new DM was initiated by someone else, show a notification
          const isInitiatedByOther = 
            data.initiatedBy !== user._id && 
            String(data.initiatedBy) !== String(user._id);
          
          console.log('Initiated by other user:', isInitiatedByOther, 'Initiator:', data.initiatedBy);
          
          if (isInitiatedByOther) {
            // Find the other user in the channel (not the current user)
            const otherUser = data.channel.members.find(member => 
              member._id !== user._id && String(member._id) !== String(user._id)
            );
            
            const displayName = otherUser ? (otherUser.name || otherUser.username) : 'Someone';
            
            console.log('Showing toast notification for new DM from:', displayName);
            toast.info(t('comms.newDirectMessageReceived', { username: displayName }));
          }
        }
      } catch (error) {
        console.error('Error handling new direct message:', error);
      }
    };
    
    // Listen for new team channels
    const handleNewTeamChannel = (data) => {
      console.log('New team channel received:', data);
      
      // Always refresh channels when a new team channel is created
      fetchChannels();
      
      // Show notification if created by someone else
      const isCreatedByOther = 
        data.createdBy !== user?._id && 
        String(data.createdBy) !== String(user?._id);
      
      if (isCreatedByOther) {
        toast.info(t('comms.newTeamChannelCreated', { 
          channelName: data.channel.name 
        }));
      }
    };
    
    // Listen for new restricted channels
    const handleNewRestrictedChannel = (data) => {
      console.log("Received new restricted channel:", data);
      
      // If we have channel data, add it to our channels state immediately
      if (data.channel) {
        const newChannel = {
          ...data.channel,
          members: data.channel.members ? deduplicateMembers(data.channel.members) : []
        };
        
        // Update all channels list
        setChannels(prevChannels => {
          // Only add if not already in the list
          if (!prevChannels.some(ch => ch._id === newChannel._id)) {
            return [newChannel, ...prevChannels];
          }
          return prevChannels;
        });
        
        // Add to private channels list
        setPrivateChannels(prevChannels => {
          if (!prevChannels.some(ch => ch._id === newChannel._id)) {
            return [newChannel, ...prevChannels];
          }
          return prevChannels;
        });
      }
      
      // Fetch all channels to ensure consistency
      fetchChannels();
      
      // Show notification only if the channel was created by someone else
      if (data.channel.createdBy !== user?._id) {
        toast.info(t('comms.restrictedChannelCreated', { channelName: data.channel.name }));
      }
    };
    
    // Listen for channel lock events
    const handleChannelLocked = (data) => {
      console.log('Channel locked:', data);
      
      // Update the locked status in the local state
      if (selectedChannel && selectedChannel._id === data.channel._id) {
        setSelectedChannel(data.channel);
      }
      
      // Refresh all channels
      fetchChannels();
      
      // Show notification if locked by someone else
      const isLockedByOther = 
        data.lockedBy !== user?._id && 
        String(data.lockedBy) !== String(user?._id);
      
      if (isLockedByOther) {
        toast.info(t('comms.channelLocked', { 
          channelName: data.channel.name 
        }));
      }
    };
    
    // Listen for channel unlock events
    const handleChannelUnlocked = (data) => {
      console.log('Channel unlocked:', data);
      
      // Update the locked status in the local state
      if (selectedChannel && selectedChannel._id === data.channel._id) {
        setSelectedChannel(data.channel);
      }
      
      // Refresh all channels
      fetchChannels();
      
      // Show notification if unlocked by someone else
      const isUnlockedByOther = 
        data.unlockedBy !== user?._id && 
        String(data.unlockedBy) !== String(user?._id);
      
      if (isUnlockedByOther) {
        toast.info(t('comms.channelUnlocked', { 
          channelName: data.channel.name 
        }));
      }
    };
    
    // Listen for channel delete events
    const handleChannelDeleted = (data) => {
      console.log('Channel deleted:', data);
      
      // If the deleted channel is the currently selected one, unselect it
      if (selectedChannel && selectedChannel._id === data.channelId) {
        setSelectedChannel(null);
      }
      
      // Refresh all channels
      fetchChannels();
      
      // Show notification if deleted by someone else
      const isDeletedByOther = 
        data.deletedBy !== user?._id && 
        String(data.deletedBy) !== String(user?._id);
      
      if (isDeletedByOther) {
        toast.info(t('comms.channelDeleted'));
      }
    };
    
    // Listen for channel order update events
    const handleChannelOrderUpdated = (data) => {
      console.log('Channel order updated:', data);
      
      // Refresh channels to get the new order
      fetchChannels();
      
      // Show notification if updated by someone else
      const isUpdatedByOther = 
        data.updatedBy !== user?._id && 
        String(data.updatedBy) !== String(user?._id);
      
      if (isUpdatedByOther) {
        toast.info(t('comms.channelOrderUpdated'));
      }
    };
    
    // Register for notifications
    socketManager.addEventListener('new-direct-message', handleNewDirectMessage);
    socketManager.addEventListener('new-team-channel', handleNewTeamChannel);
    socketManager.addEventListener('new-restricted-channel', handleNewRestrictedChannel);
    socketManager.addEventListener('channel-locked', handleChannelLocked);
    socketManager.addEventListener('channel-unlocked', handleChannelUnlocked);
    socketManager.addEventListener('channel-deleted', handleChannelDeleted);
    socketManager.addEventListener('channel-order-updated', handleChannelOrderUpdated);
    
    return () => {
      // Cleanup
      socketManager.removeEventListener('new-direct-message', handleNewDirectMessage);
      socketManager.removeEventListener('new-team-channel', handleNewTeamChannel);
      socketManager.removeEventListener('new-restricted-channel', handleNewRestrictedChannel);
      socketManager.removeEventListener('channel-locked', handleChannelLocked);
      socketManager.removeEventListener('channel-unlocked', handleChannelUnlocked);
      socketManager.removeEventListener('channel-deleted', handleChannelDeleted);
      socketManager.removeEventListener('channel-order-updated', handleChannelOrderUpdated);
    };
  }, [user, t]);

  useEffect(() => {
    // On mobile, hide sidebar when a channel is selected
    if (isMobile && selectedChannel) {
      setSidebarVisible(false);
    }
    // On desktop, always show sidebar
    if (!isMobile) {
      setSidebarVisible(true);
    }
  }, [selectedChannel, isMobile]);

  // Add polling to periodically check for new channels
  useEffect(() => {
    // Initial fetch is handled in a separate useEffect
    
    // Set up polling for channels (every 30 seconds)
    const interval = setInterval(() => {
      if (refetchOnSocketEvents) {
        console.log('Polling for channel updates...');
        // Use a different function for polling that doesn't auto-select
        const pollChannels = async () => {
          try {
            const response = await channelApi.getUserChannels();
            
            // Process all channels to remove duplicate members
            const processedChannels = response.channels?.map(channel => ({
              ...channel,
              members: channel.members ? deduplicateMembers(channel.members) : []
            })) || [];
            
            const processedTeamChannels = response.teamChannels?.map(channel => ({
              ...channel,
              members: channel.members ? deduplicateMembers(channel.members) : []
            })) || [];
            
            const processedPrivateChannels = response.privateChannels?.map(channel => ({
              ...channel,
              members: channel.members ? deduplicateMembers(channel.members) : []
            })) || [];
            
            const processedLockedChannels = response.lockedChannels?.map(channel => ({
              ...channel,
              members: channel.members ? deduplicateMembers(channel.members) : []
            })) || [];
            
            // Just update the channel lists without changing selection
            setChannels(processedChannels);
            setTeamChannels(processedTeamChannels);
            setPrivateChannels(processedPrivateChannels);
            setLockedChannels(processedLockedChannels);
            
            // Only update the current channel data if it exists
            if (selectedChannel) {
              const updatedSelected = processedChannels.find(
                ch => ch._id === selectedChannel._id
              );
              if (updatedSelected && JSON.stringify(updatedSelected) !== JSON.stringify(selectedChannel)) {
                setSelectedChannel(updatedSelected);
              }
            }
          } catch (error) {
            console.error('Error polling channels:', error);
          }
        };
        
        pollChannels();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [refetchOnSocketEvents, selectedChannel]);

  // Separate useEffect for initial fetch
  useEffect(() => {
    fetchChannels();
  }, []);

  const handleChannelClick = (channel) => {
    setSelectedChannel(channel);
    
    // Mark messages as read when selecting a channel
    if (channel && channel._id) {
      try {
        messageApi.markAsRead(channel._id);
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    }
  };

  const handleCreateChannel = async (channelData) => {
    try {
      const response = await channelApi.create(channelData);
      toast.success(t('comms.channelCreatedSuccess'));
      
      // Set the selected channel to the newly created channel immediately
      // The server is already returning populated members
      setSelectedChannel(response.channel);
      
      // Also immediately update the channels lists to ensure it appears in the UI
      setChannels(prevChannels => [response.channel, ...prevChannels]);
      
      // Add the channel to the appropriate category based on its properties
      if (response.channel.type === 'team') {
        if (response.channel.isRestrictedAccess) {
          console.log('Adding new restricted channel to privateChannels');
          setPrivateChannels(prevChannels => [response.channel, ...prevChannels]);
        } else {
          console.log('Adding new team channel to teamChannels');
          setTeamChannels(prevChannels => [response.channel, ...prevChannels]);
        }
      } else if (response.channel.type === 'private') {
        console.log('Adding new direct message channel to privateChannels');
        setPrivateChannels(prevChannels => [response.channel, ...prevChannels]);
      }
      
      // Close the dialog immediately
      setShowCreateDialog(false);
      
      // Still fetch all channels to ensure consistency, but do it after showing the channel
      fetchChannels();
    } catch (error) {
      console.error('Error creating channel:', error);
      toast.error(t('error.creatingChannel'));
    }
  };

  const handleUpdateTeamChannels = async () => {
    try {
      setUpdatingChannels(true);
      await channelApi.updateTeamChannelsMembers();
      toast.success(t('comms.teamChannelsUpdated'));
      fetchChannels();
    } catch (error) {
      console.error('Error updating team channels:', error);
      toast.error(t('error.updatingTeamChannels'));
    } finally {
      setUpdatingChannels(false);
    }
  };

  const handleCreateDirectMessage = async (selectedUser) => {
    if (!selectedUser || !selectedUser._id) return;
    
    try {
      // First check if we already have a DM channel with this user
      const existingDM = privateChannels.find(channel => {
        // DM channels have exactly 2 members
        if (channel.members && channel.members.length === 2) {
          // Check if the selected user is one of the members
          return channel.members.some(member => {
            const memberId = typeof member === 'object' ? member._id : member;
            return String(memberId) === String(selectedUser._id);
          });
        }
        return false;
      });
      
      if (existingDM) {
        // If we already have a DM channel, just select it
        console.log('DM channel already exists, selecting it:', existingDM);
        setSelectedChannel(existingDM);
        toast.info(t('comms.openingExistingConversation', { username: selectedUser.name || selectedUser.username }));
        return;
      }
      
      // Otherwise, create a new DM channel
      setCreatingDirectMessage(true);
      const response = await channelApi.getOrCreateDirectMessage(selectedUser._id);
      
      // Refresh channels to make sure the new DM is in the list
      await fetchChannels();
      
      // Select the DM channel
      setSelectedChannel(response.channel);
      toast.success(t('comms.directMessageCreated', { username: selectedUser.name || selectedUser.username }));
    } catch (error) {
      console.error('Error creating direct message:', error);
      toast.error(t('error.creatingDirectMessage'));
    } finally {
      setCreatingDirectMessage(false);
    }
  };
  
  const handleLockChannel = async (channelId) => {
    try {
      const response = await channelApi.lockChannel(channelId);
      
      // Update the selected channel immediately
      if (selectedChannel && selectedChannel._id === channelId) {
        setSelectedChannel(response.channel);
      }
      
      toast.success(t('comms.channelLocked', { channelName: response.channel.name }));
      fetchChannels();
    } catch (error) {
      console.error('Error locking channel:', error);
      toast.error(t('error.lockingChannel'));
    }
  };
  
  const handleUnlockChannel = async (channelId) => {
    try {
      const response = await channelApi.unlockChannel(channelId);
      
      // Update the selected channel immediately
      if (selectedChannel && selectedChannel._id === channelId) {
        setSelectedChannel(response.channel);
      }
      
      toast.success(t('comms.channelUnlocked', { channelName: response.channel.name }));
      fetchChannels();
    } catch (error) {
      console.error('Error unlocking channel:', error);
      toast.error(t('error.unlockingChannel'));
    }
  };
  
  const handleDeleteChannel = async (channelId) => {
    try {
      await channelApi.delete(channelId);
      
      // If the deleted channel is the selected one, clear the selection
      if (selectedChannel && selectedChannel._id === channelId) {
        setSelectedChannel(null);
      }
      
      toast.success(t('comms.channelDeletedSuccess'));
      fetchChannels();
    } catch (error) {
      console.error('Error deleting channel:', error);
      toast.error(t('error.deletingChannel'));
    }
  };

  const handleEditChannel = async (channelId, channelData) => {
    try {
      const response = await channelApi.update(channelId, channelData);
      
      // Update the selected channel if it's the one being edited
      if (selectedChannel && selectedChannel._id === channelId) {
        setSelectedChannel(response.channel);
      }
      
      toast.success(t('comms.channelUpdatedSuccess'));
      fetchChannels();
      return response;
    } catch (error) {
      console.error('Error updating channel:', error);
      toast.error(t('error.updatingChannel'));
      throw error;
    }
  };

  const handleChannelsReordered = (reorderedTeamChannels) => {
    // Update our local state immediately for a responsive UI
    setTeamChannels(reorderedTeamChannels);
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  // Add a function to specifically close the sidebar
  const closeSidebar = () => {
    if (isMobile && sidebarVisible) {
      setSidebarVisible(false);
    }
  };

  const handleInviteUsers = async (channelId, userIds) => {
    try {
      const response = await channelApi.inviteUsers(channelId, userIds);
      
      // If we have the users data, update the channel members immediately
      if (response.channel) {
        // Update the selected channel if it's the one we just modified
        if (selectedChannel && selectedChannel._id === channelId) {
          setSelectedChannel({
            ...selectedChannel,
            members: deduplicateMembers(response.channel.members)
          });
        }
        
        // Update the channel in our channels lists
        const updateChannelInList = (list, updatedChannel) => {
          return list.map(ch => 
            ch._id === updatedChannel._id ? 
              { ...ch, members: deduplicateMembers(updatedChannel.members) } : 
              ch
          );
        };
        
        setChannels(prevChannels => updateChannelInList(prevChannels, response.channel));
        setTeamChannels(prevChannels => updateChannelInList(prevChannels, response.channel));
        setPrivateChannels(prevChannels => updateChannelInList(prevChannels, response.channel));
      }
      
      toast.success(t('comms.usersInvitedSuccess'));
      fetchChannels();
      return true;
    } catch (error) {
      console.error('Error inviting users:', error);
      toast.error(t('error.invitingUsers'));
      return false;
    }
  };

  const handleRemoveUser = async (channelId, userId) => {
    try {
      const response = await channelApi.removeMember(channelId, userId);
      
      // If we have the updated channel data, update our state immediately
      if (response.channel) {
        // Update the selected channel if it's the one we just modified
        if (selectedChannel && selectedChannel._id === channelId) {
          setSelectedChannel({
            ...selectedChannel,
            members: deduplicateMembers(response.channel.members)
          });
        }
        
        // Update the channel in our channels lists
        const updateChannelInList = (list, updatedChannel) => {
          return list.map(ch => 
            ch._id === updatedChannel._id ? 
              { ...ch, members: deduplicateMembers(updatedChannel.members) } : 
              ch
          );
        };
        
        setChannels(prevChannels => updateChannelInList(prevChannels, response.channel));
        setTeamChannels(prevChannels => updateChannelInList(prevChannels, response.channel));
        setPrivateChannels(prevChannels => updateChannelInList(prevChannels, response.channel));
      }
      
      toast.success(t('comms.userRemovedSuccess'));
      fetchChannels();
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error(t('error.removingUser'));
    }
  };

  if (loading && channels.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container 
      maxWidth={true} 
      disableGutters 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 0,
        height: '100%',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#121212' : '#f5f5f7',
      }}
    >
      {/* Loading Overlay */}
      {creatingDirectMessage && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(0, 0, 0, 0.7)', 
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress color="inherit" sx={{ mb: 2 }} />
          <Typography>{t('comms.creatingDirectMessage')}</Typography>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        overflow: 'hidden',
        height: '100%',
      }}>
        {/* Channel Sidebar */}
        <ChannelSidebar 
          teamChannels={teamChannels}
          privateChannels={privateChannels}
          lockedChannels={lockedChannels}
          selectedChannel={selectedChannel}
          onChannelClick={handleChannelClick}
          onCreateChannelClick={() => setShowCreateDialog(true)}
          onCreateDirectMessage={handleCreateDirectMessage}
          visible={sidebarVisible}
          isMobile={isMobile}
          onChannelsReordered={handleChannelsReordered}
          onCloseSidebar={closeSidebar}
          onInviteClick={() => setShowInviteDialog(true)}
          onRemoveClick={() => setShowRemoveDialog(true)}
        />

        {/* Main Chat Area */}
        <ChatWindow 
          channel={selectedChannel}
          onToggleSidebar={toggleSidebar}
          sidebarVisible={sidebarVisible}
          isMobile={isMobile}
          onStartDirectMessage={handleCreateDirectMessage}
          onLockChannel={handleLockChannel}
          onUnlockChannel={handleUnlockChannel}
          onDeleteChannel={handleDeleteChannel}
          onEditChannel={handleEditChannel}
          onRemoveUser={handleRemoveUser}
        />
      </Box>

      {/* Dialogs */}
      <CreateChannelDialog 
        open={showCreateDialog} 
        onClose={() => setShowCreateDialog(false)} 
        onCreate={handleCreateChannel}
      />

      {user && user.isAdmin && (
        <>
          <InviteUserDialog
            open={showInviteDialog}
            onClose={() => setShowInviteDialog(false)}
            channels={channels}
            onInviteUsers={handleInviteUsers}
          />

          <RemoveUserDialog
            open={showRemoveDialog}
            onClose={() => setShowRemoveDialog(false)}
            channels={channels}
            onRemoveUser={handleRemoveUser}
          />
        </>
      )}
    </Container>
  );
};

export default CommsPage; 